import { useQuery } from '@apollo/client';

import mergeConnectionResultsBy from 'common/graphql/mergeConnectionResultsBy';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import useCompanyContext from 'common/hooks/useCompanyContext';

import { BANK_STATEMENT_LIMIT } from '../utils';
import { BankAndFeeStatementsDocument } from './graphql/BankAndFeeStatements.gql';

const mergeBankAccountStatementConnectionResult = mergeConnectionResultsBy(
  'bankAccountStatementId',
);

const useFetchBankAndFeeStatements = (bankAccountId?: string) => {
  const { companyProfileId } = useCompanyContext();
  const {
    data,
    error,
    fetchMore: apolloFetchMore,
    loading,
  } = useQuery(BankAndFeeStatementsDocument, {
    skip: !companyProfileId,
    variables: {
      after: null,
      companyProfileId: companyProfileId as string,
      first: BANK_STATEMENT_LIMIT,
      status: 'VALIDATED',
    },
  });

  const bankStatements =
    data?.viewer.bankAccountStatements.edges.map(({ node }) => node) ?? [];

  const filteredBankStatements = bankAccountId
    ? bankStatements.filter((bs) => bs.bankAccountId === bankAccountId)
    : bankStatements;

  const endCursor = data?.viewer?.bankAccountStatements?.pageInfo?.endCursor;
  const hasNextPage =
    data?.viewer?.bankAccountStatements?.pageInfo?.hasNextPage;
  const feeStatements = data?.viewer?.company?.feeStatements || [];

  const fetchMore = () =>
    apolloFetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) =>
        mergeBankAccountStatementConnectionResult(
          previousResult,
          fetchMoreResult,
        ),
      variables: { after: endCursor, first: BANK_STATEMENT_LIMIT },
    });

  const {
    bankAccounts,
    error: bankAccountsError,
    loading: bankAccountsLoading,
  } = useCompanyBankAccounts({
    includeClosedBankAccounts: true,
  });

  return {
    bankAccounts,
    bankStatements: filteredBankStatements,
    endCursor,
    error: error || bankAccountsError,
    feeStatements,
    fetchMore,
    hasNextPage,
    loading: loading || bankAccountsLoading,
    totalStatementsCount: bankStatements.length + feeStatements.length,
  };
};

export default useFetchBankAndFeeStatements;
